import { PlayLayout } from '@app/play-now/routes/PlayLayout';
import { getRedirectRoute, setupPlayUtils } from '@app/play-now/routes/remix-route-helper';
import { ClientMetaFunction, mergeMeta } from '@app/utils/remix/play-now-meta';
import { loadShowDetail } from '@play-now/video/api/load-show-detail';
import { getRemixMeta } from '@play-now/video/components/Meta/PlayMetaData';
import { toBroadcastEventItemListSchema } from '@play-now/video/components/PageMetaData/JsonLd';
import { useInitialData } from '@play-now/video/config/VideoAppContext';
import { notFoundPath } from '@play-now/video/pages/Error/PageNotFound.route';
import { ShowBroadcastPage } from '@play-now/video/pages/ShowBroadcast/ShowBroadcastPage';
import { showBroadcastPath } from '@play-now/video/pages/ShowBroadcast/ShowBroadcastPage.route';
import { LoaderFunctionArgs, redirect } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';

export const loader = async (args: LoaderFunctionArgs) => {
  const {
    query,
    axios,
    config,
    i18n,
    request: { url },
  } = setupPlayUtils(args);
  const showId = query.id;
  // Redirect to 404 page if no showId is provided
  if (!showId.length) return redirect(config.baseUrl + notFoundPath());

  const show = await loadShowDetail(showId, axios);

  // Redirect to show page page with full path if needed
  const redirectResponse = getRedirectRoute({ fullPath: config.baseUrl + showBroadcastPath(i18n, show), url });
  if (redirectResponse) return redirectResponse;

  const title = `${i18n.t('global.broadcastsDates')} - ${show.title}`;
  const metaData = getRemixMeta({
    config,
    title,
    allowIndexing: false,
    'script:ld+json': show.programList
      ? toBroadcastEventItemListSchema(show, config.language, config.baseUrl, config.modernImagesBaseUrl)
      : undefined,
  });
  return { show, meta: metaData };
};

export const meta: ClientMetaFunction<typeof loader> = ({ data, matches }) => mergeMeta({ data, matches });

const ShowBroadcastPageRoute = () => {
  const { show } = useLoaderData<typeof loader>();
  const initialData = useInitialData();
  return (
    <PlayLayout pageId='show'>
      <ShowBroadcastPage initialData={{ ...initialData, show }} />
    </PlayLayout>
  );
};

export default ShowBroadcastPageRoute;
